import React, { FC, useState, useRef, useEffect } from 'react'
import styles from "./Image.module.scss"
interface ImageProps extends React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> {
    fade?: boolean
}

type ObserverCallback = (isVisible: boolean) => void
const observerCallbacks = new Map<Element, ObserverCallback>()
let sharedObserver: IntersectionObserver | null = null

function getSharedObserver(): IntersectionObserver | null {
    if (!('IntersectionObserver' in window)) return null
    if (!sharedObserver) {
        sharedObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const cb = observerCallbacks.get(entry.target)
                    if (cb) cb(entry.isIntersecting)
                })
            },
            { rootMargin: '100px' }
        )
    }
    return sharedObserver
}

const Image: FC<ImageProps> = (props) => {
    const [onLoadImage, setonLoadImage] = useState(false)
    const [isVisible, setIsVisible] = useState(() => !('IntersectionObserver' in window))
    const imgRef = useRef<HTMLImageElement>(null)

    useEffect(() => {
        const observer = getSharedObserver()
        if (!observer) return

        const el = imgRef.current
        if (!el) return

        observerCallbacks.set(el, (visible) => {
            if (visible) {
                setIsVisible(true)
                observerCallbacks.delete(el)
                observer.unobserve(el)
            }
        })
        observer.observe(el)

        return () => {
            observerCallbacks.delete(el)
            observer.unobserve(el)
        }
    }, [])

    const { src, onError, ...rest } = props
    return (
        <img
            ref={imgRef}
            onLoad={() => { setonLoadImage(true) }}
            {...rest}
            src={isVisible ? src : 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'}
            onError={isVisible ? onError : undefined}
            className={`${props.className} ${onLoadImage ? styles.showImage : styles.hideImage}`}
            alt=""
        />
    )
}

export default Image
